<template>
  <v-container fluid class="communication-log pa-4">
    <!-- Search Card -->
    <v-card class="search-card mb-4" elevation="2">
      <v-card-title class="search-header">
        <v-icon left color="white">mdi-magnify</v-icon>
        Communication Log Search
      </v-card-title>
      <v-card-text class="pt-4">
        <v-layout row wrap align-center class="px-2">
          <v-flex xs12 md3 class="px-2">
            <v-text-field
              v-model="searchAccountID"
              label="Account ID"
              outlined
              dense
              clearable
              prepend-inner-icon="mdi-account"
              placeholder="Enter Account ID"
              @keyup.enter="search"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 md1 class="text-center px-2">
            <span class="grey--text font-weight-medium">OR</span>
          </v-flex>
          <v-flex xs12 md3 class="px-2">
            <v-text-field
              v-model="searchPhoneNumber"
              label="Phone Number"
              outlined
              dense
              clearable
              prepend-inner-icon="mdi-phone"
              placeholder="Enter Phone Number"
              @keyup.enter="search"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 md2 class="px-2">
            <v-menu
              ref="fromDateMenu"
              v-model="fromDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="fromDate"
                  label="From Date"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  dense
                  outlined
                  clearable
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="fromDate"
                no-title
                @input="fromDateMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-flex>
          <v-flex xs12 md3 class="px-2">
            <v-btn
              color="primary"
              large
              :loading="loading"
              :disabled="!canSearch"
              @click="search"
              class="search-btn"
            >
              <v-icon left>mdi-magnify</v-icon>
              Search Communications
            </v-btn>
          </v-flex>
        </v-layout>
      </v-card-text>
    </v-card>

    <!-- Results Section -->
    <v-layout row wrap v-if="hasSearched">
      <!-- Calls Panel -->
      <v-flex xs12 lg6 class="pr-2 pb-3">
        <v-card class="results-card calls-card" elevation="2">
          <v-card-title class="calls-header">
            <v-icon left color="white">mdi-phone</v-icon>
            Calls
            <v-spacer></v-spacer>
            <v-chip color="white" text-color="#1976D2" small>
              {{ filteredCalls.length }} records
            </v-chip>
          </v-card-title>

          <!-- Calls Filters -->
          <v-card-text class="filters-section pb-0">
            <v-layout row wrap>
              <v-flex xs12 sm4 class="pr-2">
                <v-menu
                  ref="callsDateMenu"
                  v-model="callsDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="callsDateFilter"
                      label="Filter by Date"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      dense
                      outlined
                      clearable
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="callsDateFilter"
                    no-title
                    @input="callsDateMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-flex>
              <v-flex xs12 sm4 class="pr-2">
                <v-select
                  v-model="callsStatusFilter"
                  :items="callStatusOptions"
                  label="Filter by Status"
                  dense
                  outlined
                  clearable
                  prepend-inner-icon="mdi-filter"
                ></v-select>
              </v-flex>
              <v-flex xs12 sm4>
                <v-select
                  v-model="callsDirectionFilter"
                  :items="directionOptions"
                  label="Filter by Direction"
                  dense
                  outlined
                  clearable
                  prepend-inner-icon="mdi-swap-horizontal"
                ></v-select>
              </v-flex>
            </v-layout>
          </v-card-text>

          <!-- Calls Table -->
          <v-data-table
            :headers="callsHeaders"
            :items="filteredCalls"
            :loading="loadingCalls"
            :rows-per-page-items="[5, 10, 25, 50, 100]"
            class="elevation-0"
          >
            <template v-slot:items="props">
              <tr>
                <td class="cell-compact">
                  <v-chip
                    :color="props.item.direction === 'INC' ? 'info' : 'success'"
                    small
                    text-color="white"
                    class="mb-1"
                  >
                    <v-icon small left>
                      {{
                        props.item.direction === "INC"
                          ? "mdi-phone-incoming"
                          : "mdi-phone-outgoing"
                      }}
                    </v-icon>
                    {{ props.item.direction === "INC" ? "In" : "Out" }}
                  </v-chip>
                  <div class="cell-date">
                    {{ formatDateTime(props.item.date) }}
                  </div>
                </td>
                <td class="cell-compact">
                  <a
                    v-if="props.item.accountID"
                    href="#"
                    @click.prevent="goToAccount(props.item)"
                    class="account-link"
                    >{{ props.item.accountID }}</a
                  >
                  <span v-else>-</span>
                  <div class="cell-subtext">{{ props.item.name || "" }}</div>
                </td>
                <td class="cell-compact">
                  <div>{{ props.item.from || "-" }}</div>
                  <div class="cell-subtext">→ {{ props.item.to || "-" }}</div>
                </td>
                <td>{{ props.item.type || "-" }}</td>
                <td>
                  <v-chip
                    :color="getStatusColor(props.item.status)"
                    small
                    text-color="white"
                    >{{ props.item.status || "N/A" }}</v-chip
                  >
                </td>
                <td>
                  <v-btn
                    v-if="props.item.recording"
                    icon
                    small
                    color="primary"
                    @click="playRecording(props.item.recording)"
                  >
                    <v-icon small>mdi-play-circle</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
            <template v-slot:no-data>
              <div class="text-center py-4">
                <v-icon large color="grey lighten-1">mdi-phone-off</v-icon>
                <div class="grey--text mt-2">No calls found</div>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-flex>

      <!-- SMS Panel -->
      <v-flex xs12 lg6 class="pl-2 pb-3">
        <v-card class="results-card sms-card" elevation="2">
          <v-card-title class="sms-header">
            <v-icon left color="white">mdi-message-text</v-icon>
            SMS Messages
            <v-spacer></v-spacer>
            <v-chip color="white" text-color="#43A047" small>
              {{ filteredSms.length }} records
            </v-chip>
          </v-card-title>

          <!-- SMS Filters -->
          <v-card-text class="filters-section pb-0">
            <v-layout row wrap>
              <v-flex xs12 sm4 class="pr-2">
                <v-menu
                  ref="smsDateMenu"
                  v-model="smsDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="smsDateFilter"
                      label="Filter by Date"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      dense
                      outlined
                      clearable
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="smsDateFilter"
                    no-title
                    @input="smsDateMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-flex>
              <v-flex xs12 sm4 class="pr-2">
                <v-select
                  v-model="smsStatusFilter"
                  :items="smsStatusOptions"
                  label="Filter by Status"
                  dense
                  outlined
                  clearable
                  prepend-inner-icon="mdi-filter"
                ></v-select>
              </v-flex>
              <v-flex xs12 sm4>
                <v-select
                  v-model="smsDirectionFilter"
                  :items="directionOptions"
                  label="Filter by Direction"
                  dense
                  outlined
                  clearable
                  prepend-inner-icon="mdi-swap-horizontal"
                ></v-select>
              </v-flex>
            </v-layout>
          </v-card-text>

          <!-- SMS Table -->
          <v-data-table
            :headers="smsHeaders"
            :items="filteredSms"
            :loading="loadingSms"
            :rows-per-page-items="[5, 10, 25, 50, 100]"
            class="elevation-0"
          >
            <template v-slot:items="props">
              <tr>
                <td class="cell-compact">
                  <v-chip
                    :color="props.item.direction === 'INC' ? 'info' : 'success'"
                    small
                    text-color="white"
                    class="mb-1"
                  >
                    <v-icon small left>
                      {{
                        props.item.direction === "INC"
                          ? "mdi-message-arrow-left"
                          : "mdi-message-arrow-right"
                      }}
                    </v-icon>
                    {{ props.item.direction === "INC" ? "In" : "Out" }}
                  </v-chip>
                  <div class="cell-date">
                    {{ formatDateTime(props.item.dateTime) }}
                  </div>
                </td>
                <td class="cell-compact">
                  <a
                    v-if="props.item.accountId"
                    href="#"
                    @click.prevent="goToAccountFromSms(props.item)"
                    class="account-link"
                    >{{ props.item.accountId }}</a
                  >
                  <span v-else>-</span>
                  <div class="cell-subtext">{{ props.item.name || "" }}</div>
                </td>
                <td class="cell-compact">
                  <div>{{ props.item.from || "-" }}</div>
                  <div class="cell-subtext">→ {{ props.item.to || "-" }}</div>
                </td>
                <td class="cell-message">
                  <div class="message-text" :title="props.item.text">
                    {{ props.item.text || "-" }}
                  </div>
                </td>
                <td>
                  <v-chip
                    :color="getSmsStatusColor(props.item.status)"
                    small
                    text-color="white"
                    >{{ props.item.status || "N/A" }}</v-chip
                  >
                </td>
                <td>
                  <v-btn
                    v-if="props.item.attachmentUrls"
                    icon
                    small
                    color="primary"
                    @click="viewAttachment(props.item)"
                  >
                    <v-icon small>mdi-paperclip</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
            <template v-slot:no-data>
              <div class="text-center py-4">
                <v-icon large color="grey lighten-1">mdi-message-off</v-icon>
                <div class="grey--text mt-2">No SMS messages found</div>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- Empty State -->
    <v-card v-if="!hasSearched" class="empty-state-card" elevation="1">
      <v-card-text class="text-center py-12">
        <v-icon size="80" color="grey lighten-1">mdi-forum-outline</v-icon>
        <h3 class="grey--text text--darken-1 mt-4">
          Search for Communications
        </h3>
        <p class="grey--text mt-2">
          Enter an Account ID or Phone Number above to view all calls and SMS
          messages.
        </p>
      </v-card-text>
    </v-card>

    <!-- Audio Player Dialog -->
    <v-dialog v-model="audioDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">
          <v-icon left>mdi-play-circle</v-icon>
          Call Recording
        </v-card-title>
        <v-card-text class="pt-4">
          <audio
            ref="audioPlayer"
            :src="currentRecording"
            controls
            style="width: 100%"
          ></audio>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="audioDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Attachment Viewer Dialog -->
    <v-dialog v-model="attachmentDialog" max-width="800">
      <v-card>
        <v-card-title class="headline">
          <v-icon left>mdi-paperclip</v-icon>
          Attachments
        </v-card-title>
        <v-card-text class="pt-4">
          <div
            v-for="(url, index) in currentAttachments"
            :key="index"
            class="mb-3"
          >
            <v-img :src="url" max-height="400" contain></v-img>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="attachmentDialog = false"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from "moment";

export default {
  name: "CommunicationLog",
  data: () => ({
    // Search
    searchAccountID: "",
    searchPhoneNumber: "",
    fromDate: null,
    fromDateMenu: false,
    hasSearched: false,

    // Loading states
    loading: false,
    loadingCalls: false,
    loadingSms: false,

    // Data
    calls: [],
    sms: [],

    // Call Filters
    callsDateMenu: false,
    callsDateFilter: null,
    callsStatusFilter: null,
    callsDirectionFilter: null,

    // SMS Filters
    smsDateMenu: false,
    smsDateFilter: null,
    smsStatusFilter: null,
    smsDirectionFilter: null,

    // Filter Options
    directionOptions: [
      { text: "Incoming", value: "INC" },
      { text: "Outgoing", value: "OUT" },
    ],
    callStatusOptions: [
      "completed",
      "busy",
      "failed",
      "no-answer",
      "canceled",
      "machine",
      "human",
    ],
    smsStatusOptions: [
      "DELIVERED",
      "SENT",
      "QUEUED",
      "PENDING",
      "FAILED",
      "UNDELIVERED",
      "ERROR",
    ],

    // Table Headers
    callsHeaders: [
      { text: "Date", value: "date" },
      { text: "Account", value: "accountID" },
      { text: "From / To", value: "from" },
      { text: "Type", value: "type" },
      { text: "Status", value: "status" },
      { text: "", value: "recording", sortable: false },
    ],
    smsHeaders: [
      { text: "Date", value: "dateTime" },
      { text: "Account", value: "accountId" },
      { text: "From / To", value: "from" },
      { text: "Message", value: "text" },
      { text: "Status", value: "status" },
      { text: "", value: "attachmentUrls", sortable: false },
    ],

    // Dialogs
    audioDialog: false,
    currentRecording: "",
    attachmentDialog: false,
    currentAttachments: [],
  }),
  created() {
    // Set default fromDate to 2 months ago
    this.fromDate = moment()
      .subtract(2, "months")
      .format("YYYY-MM-DD");
  },
  computed: {
    canSearch() {
      return this.searchAccountID || this.searchPhoneNumber;
    },
    filteredCalls() {
      let result = [...this.calls];

      if (this.callsDateFilter) {
        const filterDate = moment(this.callsDateFilter).startOf("day");
        result = result.filter((call) => {
          const callDate = moment(call.date).startOf("day");
          return callDate.isSame(filterDate);
        });
      }

      if (this.callsStatusFilter) {
        result = result.filter(
          (call) =>
            call.status &&
            call.status.toLowerCase() === this.callsStatusFilter.toLowerCase()
        );
      }

      if (this.callsDirectionFilter) {
        result = result.filter(
          (call) => call.direction === this.callsDirectionFilter
        );
      }

      // Sort by date descending
      result.sort((a, b) => new Date(b.date) - new Date(a.date));

      return result;
    },
    filteredSms() {
      let result = [...this.sms];

      if (this.smsDateFilter) {
        const filterDate = moment(this.smsDateFilter).startOf("day");
        result = result.filter((msg) => {
          const msgDate = moment(msg.dateTime).startOf("day");
          return msgDate.isSame(filterDate);
        });
      }

      if (this.smsStatusFilter) {
        result = result.filter(
          (msg) =>
            msg.status &&
            msg.status.toUpperCase() === this.smsStatusFilter.toUpperCase()
        );
      }

      if (this.smsDirectionFilter) {
        result = result.filter(
          (msg) => msg.direction === this.smsDirectionFilter
        );
      }

      // Sort by date descending
      result.sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime));

      return result;
    },
  },
  methods: {
    async search() {
      if (!this.canSearch) return;

      this.loading = true;
      this.loadingCalls = true;
      this.loadingSms = true;
      this.hasSearched = true;

      const payload = {
        AccountID: this.searchAccountID || null,
        PhoneNumber: this.searchPhoneNumber || null,
        fromDate: this.fromDate || null,
      };

      try {
        // Fetch calls and SMS in parallel
        const [callsResponse, smsResponse] = await Promise.all([
          this.$http.post("api/calls/getCalls", payload),
          this.$http.post("api/sms/getMessages", payload),
        ]);

        this.calls = callsResponse || [];
        this.loadingCalls = false;

        this.sms = smsResponse || [];
        this.loadingSms = false;
      } catch (error) {
        console.error("Error fetching communications:", error);
        this.calls = [];
        this.sms = [];
      } finally {
        this.loading = false;
        this.loadingCalls = false;
        this.loadingSms = false;
      }
    },
    formatDateTime(dateString) {
      if (!dateString) return "-";
      return moment(dateString).format("MM/DD/YYYY hh:mm A");
    },
    getStatusColor(status) {
      if (!status) return "grey";
      const s = status.toLowerCase();
      if (s === "completed" || s === "human") return "success";
      if (s === "busy" || s === "no-answer") return "warning";
      if (s === "failed" || s === "canceled") return "error";
      if (s === "machine") return "info";
      return "grey";
    },
    getSmsStatusColor(status) {
      if (!status) return "grey";
      const s = status.toUpperCase();
      if (s === "DELIVERED") return "success";
      if (s === "SENT") return "blue-grey";
      if (s === "QUEUED" || s === "PENDING") return "warning";
      if (s === "FAILED" || s === "UNDELIVERED" || s === "ERROR")
        return "error";
      return "grey";
    },
    playRecording(url) {
      this.currentRecording = url;
      this.audioDialog = true;
    },
    viewAttachment(item) {
      if (item.attachmentUrls) {
        this.currentAttachments = item.attachmentUrls
          .split(";")
          .filter(Boolean);
        this.attachmentDialog = true;
      }
    },
    goToAccount(item) {
      if (item.accountID) {
        this.$router.push({
          name:
            item.accountsSource === "DINNER"
              ? "DinnerAccountView"
              : "Account View",
          params: { id: item.accountID },
        });
      }
    },
    goToAccountFromSms(item) {
      if (item.accountId) {
        this.$router.push({
          name:
            item.accountsSource === "DINNER"
              ? "DinnerAccountView"
              : "Account View",
          params: { id: item.accountId },
        });
      }
    },
  },
};
</script>

<style scoped>
.communication-log {
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
  min-height: calc(100vh - 64px);
}

.search-card {
  border-radius: 12px;
  overflow: hidden;
}

.search-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
}

.search-btn {
  height: 40px !important;
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.results-card {
  border-radius: 12px;
  overflow: hidden;
}

.calls-header {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  color: white;
  font-weight: 600;
}

.sms-header {
  background: linear-gradient(135deg, #43a047 0%, #388e3c 100%);
  color: white;
  font-weight: 600;
}

.filters-section {
  background: #fafafa;
  border-bottom: 1px solid #eee;
}

.empty-state-card {
  border-radius: 12px;
}

.account-link {
  color: #1976d2;
  text-decoration: none;
  font-weight: 500;
}

.account-link:hover {
  text-decoration: underline;
}

.message-text {
  max-width: 180px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cell-compact {
  white-space: nowrap;
}

.cell-date {
  font-size: 12px;
  color: #666;
}

.cell-subtext {
  font-size: 11px;
  color: #888;
}

.cell-message {
  max-width: 200px;
}

::v-deep .v-data-table {
  font-size: 13px;
}

::v-deep .v-data-table-header th {
  background: #f5f5f5 !important;
  font-weight: 600 !important;
  white-space: nowrap;
}

::v-deep .v-data-table tbody tr:hover {
  background: #f8f9fa !important;
}
</style>
